import axios from "axios";
import {
  getLoadedTenantDocumentListsOrNull,
  setTenantDocumentLists,
} from "./tenantDocumentListsStore";

// Loads the document lists from the backend (see ./tenantDocumentListsStore.js)
// and logs what happens, with the [tenantDocumentLists] prefix:
//   info   loaded (version, try, time)
//   warn   a try failed, trying again
//   error  every try failed (the page shows a retry button)

const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;
const REQUEST_TIMEOUT_MS = 15000;

let loading = null;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchWithRetries = async () => {
  const url = `${process.env.REACT_APP_SERVER_URL}/config/tenant-document-lists`;
  const startedAt = Date.now();
  let lastError;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    try {
      const response = await axios.get(url, { timeout: REQUEST_TIMEOUT_MS });
      // Throws on an answer that does not look like the lists
      const lists = setTenantDocumentLists(response?.data?.data);
      console.info(
        `[tenantDocumentLists] loaded version ${lists.version} ` +
          `(try ${attempt}, ${Date.now() - startedAt} ms)`
      );
      return lists;
    } catch (error) {
      lastError = error;
      if (attempt < MAX_ATTEMPTS) {
        const delay = RETRY_DELAY_MS * attempt;
        console.warn(
          `[tenantDocumentLists] try ${attempt} of ${MAX_ATTEMPTS} failed ` +
            `(${error?.message}), trying again in ${delay} ms`
        );
        await wait(delay);
      }
    }
  }
  console.error(
    `[tenantDocumentLists] could not load after ${MAX_ATTEMPTS} tries`,
    lastError
  );
  throw lastError;
};

// Resolves with the lists. Asks the backend once per page load (callers at
// the same time share one request) and tries up to 3 times. Rejects when all
// tries fail; the next call tries again.
export const loadTenantDocumentLists = () => {
  const loaded = getLoadedTenantDocumentListsOrNull();
  if (loaded) return Promise.resolve(loaded);
  if (!loading) {
    loading = fetchWithRetries().finally(() => {
      loading = null;
    });
  }
  return loading;
};
